/**
 * This is a wrapper component for different Layouts.
 * Navigational 'aside' content should be added to this wrapper.
 */
import React, { useEffect, useRef, useState } from 'react';

import { FormattedMessage } from '../../../util/reactIntl';

import { TabNav } from '../..';

import { createGlobalState } from './hookGlobalState';

import css from './LayoutSideNavigation.module.css';
import {
  ApartmentOutlined,
  AppstoreOutlined,
  WalletOutlined,
  CalendarOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  FileAddOutlined,
  PicCenterOutlined,
  FileSearchOutlined,
  MailOutlined,
  PoweroffOutlined,
  SafetyOutlined,
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Badge, Button, Menu } from 'antd';
import { useHistory } from 'react-router-dom';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { createResourceLocatorString, pathByRouteName } from '../../../util/routes';
import appSettings from '../../../config/settings';
import { logout } from '../../../ducks/auth.duck';
import { loadData as loadInboxData } from '../../../containers/InboxPage/InboxPage.duck';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { isScrollingDisabled } from '../../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../../ducks/marketplaceData.duck';
import { userTypeOf } from '../../../util/userType';
import { teamRoleOf } from '../../../util/teamRole';
import PreferredNetworkNavItem from '../../../containers/PreferredNetwork/PreferredNetworkNavItem';
import { isNetworkPath, networkNavFor } from '../../../containers/PreferredNetwork/networkPages';

// The Preferred Network item's key. Its pages open from its own flyout, not from a menu click.
const NETWORK_ITEM_KEY = 'pn';
// A candidate's My Network, opened directly.
const MY_NETWORK_ITEM_KEY = 'mn';

const MAX_HORIZONTAL_NAV_SCREEN_WIDTH = 1023;

// Add global state for tab scrolling effect
const initialScrollState = { scrollLeft: 0 };
const { useGlobalState } = createGlobalState(initialScrollState);

// Horizontal scroll animation using element.scrollTo()
const scrollToTab = (currentPage, scrollLeft, setScrollLeft) => {
  const el = document.querySelector(`#${currentPage}Tab`);

  if (el) {
    // el.scrollIntoView doesn't work with Safari and it considers vertical positioning too.
    // This scroll behaviour affects horizontal scrolling only
    // and it expects that the immediate parent element is scrollable.
    const parent = el.parentElement;
    const parentRect = parent.getBoundingClientRect();
    const maxScrollDistance = parent.scrollWidth - parentRect.width;

    const hasParentScrolled = parent.scrollLeft > 0;
    const scrollPositionCurrent = hasParentScrolled ? parent.scrollLeft : scrollLeft;

    const tabRect = el.getBoundingClientRect();
    const diffLeftBetweenTabAndParent = tabRect.left - parentRect.left;
    const tabScrollPosition = parent.scrollLeft + diffLeftBetweenTabAndParent;

    const scrollPositionNew =
      tabScrollPosition > maxScrollDistance
        ? maxScrollDistance
        : parent.scrollLeft + diffLeftBetweenTabAndParent;

    const needsSmoothScroll = scrollPositionCurrent !== scrollPositionNew;

    if (parent.scrollTo && (!hasParentScrolled || (hasParentScrolled && needsSmoothScroll))) {
      // Ensure that smooth scroll animation uses old position as starting point after navigation.
      parent.scrollTo({ left: scrollPositionCurrent });
      // Scroll to new position
      parent.scrollTo({ left: scrollPositionNew, behavior: 'smooth' });
    }
    // Always keep track of new position (even if smooth scrolling is not applied)
    setScrollLeft(scrollPositionNew);
  }
};

/**
 * Side nav with navigation to different account settings.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.currentPage
 * @returns {JSX.Element} Side nav with navigation to different account settings
 */
const DashboardSideNavComponent = props => {

  const { currentUser, currentUserNotificationCount=0, profileListing, totalUnreadConversations=0, conversationsBootstrapped, onBootstrapConversations } = props;
  
  const isCandidate = userTypeOf(currentUser) === 'candidate';
  const isCustomer = userTypeOf(currentUser) === 'customer';

  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const [collapsed, setCollapsed] = useState(false);

  const [selectedTab, setSelectedTab] = useState(null);
  const [openTab, setOpenTab] = useState(null);
  const [openKeys, setOpenKeys] = useState([openTab]);
  const menuRef = useRef(null);

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const [mounted, setMounted] = useState(false);
  const [scrollLeft, setScrollLeft] = useGlobalState('scrollLeft');

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (currentUser && !conversationsBootstrapped) {
      onBootstrapConversations();
    }
  }, [currentUser, conversationsBootstrapped]);

  useEffect(() => {
    if (mounted) {
      const { currentPage } = props;
      const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
      const hasHorizontalTabLayout = hasMatchMedia
        ? window.matchMedia(`(max-width: ${MAX_HORIZONTAL_NAV_SCREEN_WIDTH}px)`)?.matches
        : true;
      
      if (hasHorizontalTabLayout) {
        scrollToTab(currentPage, scrollLeft, setScrollLeft);
      }
    }
  }, [mounted]);

  const { currentPage } = props;

  useEffect(() => {
    if (collapsed) {
      document.body.classList.add('navcollapsed');
    } else {
      document.body.classList.remove('navcollapsed');
    }
  }, [collapsed]);

  // Listen for external collapse/expand events (e.g., from filter sidebar)
  useEffect(() => {
    const handleExternalCollapse = (event) => {
      const shouldCollapse = event.detail?.collapsed;
      if (typeof shouldCollapse === 'boolean') {
        setCollapsed(shouldCollapse);
      }
    };

    window.addEventListener('dashboardSideNavToggle', handleExternalCollapse);
    return () => {
      window.removeEventListener('dashboardSideNavToggle', handleExternalCollapse);
    };
  }, []);



  // A press outside the navigation closes its open submenus. The Preferred Network flyout renders in
  // the document body, outside the navigation's own element, but a press in it is a press on the
  // navigation.
  const handleClickOutside = event => {
    const inNetworkFlyout = Boolean(event.target?.closest?.('[data-network-flyout]'));
    if (menuRef.current && !menuRef.current.contains(event.target) && !inNetworkFlyout) {
      setOpenKeys([]);
    }
  };

  useEffect(() => {
    // Add event listener for clicks outside
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const currentPage = window.location.pathname.split('/')[
      window.location.pathname.split('/').length - 1
    ];

    switch (currentPage) {
      case 'dashboard':
        localStorage.setItem('selectedTab', 1);
        localStorage.setItem('openTab', 1);
        break;
      case 'wallet':
        localStorage.setItem('selectedTab', 13);
        localStorage.setItem('openTab', 13);
        break;
      case 'findJob':
        localStorage.setItem('selectedTab', 2);
        localStorage.setItem('openTab', 2);
        break;
      case 'postJob':
        localStorage.setItem('selectedTab', 3);
        localStorage.setItem('openTab', 3);
        break;
      case 'orders':
      case 'sales':
        localStorage.setItem('selectedTab', 4);
        localStorage.setItem('openTab', 4);
        break;
      case 'calendar':
        localStorage.setItem('selectedTab', 5);
        localStorage.setItem('openTab', 5);
        break;
      case 'user-manage':
        localStorage.setItem('selectedTab', 6);
        localStorage.setItem('openTab', 6);
        break;
      case 'verifications':
        localStorage.setItem('selectedTab', 7);
        localStorage.setItem('openTab', 7);
        break;
      case 'job-dashboard':
        localStorage.setItem('selectedTab', 11);
        localStorage.setItem('openTab', 11);
        break;
      case 'profile-settings':
        localStorage.setItem('selectedTab', 8);
        localStorage.setItem('openTab', 8);
        break;
      case 'contact-details':
        localStorage.setItem('selectedTab', '9a');
        localStorage.setItem('openTab', '9');
        break;
      case 'change-password':
        localStorage.setItem('selectedTab', '9b');
        localStorage.setItem('openTab', '9');
        break;
      case 'payments':
        localStorage.setItem('selectedTab', '9c');
        localStorage.setItem('openTab', '9');
        break;
      case 'payment-methods':
        localStorage.setItem('selectedTab', '9d');
        localStorage.setItem('openTab', '9');
        break;
      case 'msg-sms-consent':
        localStorage.setItem('selectedTab', '9e');
        localStorage.setItem('openTab', '9');
        break;
      case 'payments-history':
        localStorage.setItem('selectedTab', '9f');
        localStorage.setItem('openTab', '9');
        break;
      default:
        break;
    }

    const storedTab = localStorage.getItem('selectedTab');
    if (storedTab) {
      setSelectedTab(storedTab);

      if (['9a', '9b', '9c', '9d', '9e', '9f'].includes(storedTab)) {
        setOpenKeys(['9']);
      }
    }

    const openTab = localStorage.getItem('openTab');
    if (openTab) {
      setOpenKeys([openTab]);
    }

  }, []);

  const handleLogout = () => {
    const { onLogout } = props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  };

  const onMenuClick = values => {
    // Clicking Preferred Network opens its flyout; it changes nothing else in the menu.
    if (values?.key === NETWORK_ITEM_KEY) return;
    if (values?.key === MY_NETWORK_ITEM_KEY) {
      history.push(createResourceLocatorString('MyNetworkPage', routeConfiguration, {}, {}));
      return;
    }

    setSelectedTab(values?.key);
    localStorage.setItem('selectedTab', values?.key);

    if (['9a', '9b', '9c', '9d', '9e', '9f'].includes(values?.key)) {
      setOpenKeys(['9']);
      localStorage.setItem('openTab', '9');
    } else {
      setOpenKeys([]);
      localStorage.setItem('openTab', '');
    }

    let page = null;
    switch (values?.key) {
      case '1':
        page = 'DashboardPage';
        break;
      case '2':
        page = null;
        break;
      case '3':
        page = null;
        break;
      case '4':
        page = 'InboxPage';
        break;
      case '5':
        page = 'CalendarPage';
        break;
      case '6':
        page = 'UserManagementPage';
        break;
      case '7':
        page = null;
        break;
      case '11':
        page = 'JobPostsPage';
        break;
      case '12': 
        page = 'RFPPostsPage';
        break;
      case '13': 
        page = 'WalletPage';
        break;
      case '8':
        page = 'ProfileSettingsPage';
        break;
      case '9a':
        page = 'ContactDetailsPage';
        break;
      case '9b':
        page = 'PasswordChangePage';
        break;
      case '9c':
        page = 'StripePayoutPage';
        break;
      case '9d':
        page = 'PaymentMethodsPage';
        break;
      case '9e':
        page = 'TwilioConsentPage';
        break;
      case '9f':
        page = 'PaymentsHistoryPage';
        break;
      case '10':
        page = 'Logout';
        break;
      default:
        page = null;
    }

    if(page == 'Logout') {
      handleLogout();
    }else if (page) {
      if (page == 'InboxPage') {
        history.push(createResourceLocatorString(page, routeConfiguration, { tab: 'sales' }, {}));
      } else if (page === 'JobPostsPage') {
        history.push(createResourceLocatorString(page, routeConfiguration, { tab: 'job-dashboard' }, {}));
      } else if (page === 'RFPPostsPage') {
        history.push(createResourceLocatorString(page, routeConfiguration, { tab: 'rfp-dashboard' }, {}));
      } else history.push(createResourceLocatorString(page, routeConfiguration, {}));
    }
  };

  const onMenuOpen = keys => {
    setOpenKeys(keys);
    if (keys.length > 0) {
      setOpenTab(keys[keys.length - 1]);
      localStorage.setItem('openTab', keys[keys.length - 1]);
    }
  };

  const customerType = currentUser?.attributes?.profile?.publicData?.customerProfile?.customerType || profileListing?.attributes?.publicData?.customerProfile?.customerType || null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const networkNav = networkNavFor(userTypeOf(currentUser));
  const openNetworkPage = routeName =>
    history.push(createResourceLocatorString(routeName, routeConfiguration, {}, {}));
  const networkItem = !networkNav
    ? []
    : networkNav.kind === 'direct'
    ? [{ key: MY_NETWORK_ITEM_KEY, icon: <ApartmentOutlined />, label: <FormattedMessage id={networkNav.page.labelId} /> }]
    : [
        {
          key: NETWORK_ITEM_KEY,
          // Collapsed to icons, the icon opens the flyout; otherwise the item's words do.
          icon:
            !isMobile && collapsed ? (
              <PreferredNetworkNavItem
                variant="icon"
                icon={<ApartmentOutlined />}
                userType={userTypeOf(currentUser)}
                onNavigate={openNetworkPage}
              />
            ) : (
              <ApartmentOutlined />
            ),
          label: (
            <PreferredNetworkNavItem
              userType={userTypeOf(currentUser)}
              onNavigate={openNetworkPage}
            />
          ),
        },
      ];
  const onNetworkPage = isNetworkPath(history?.location?.pathname);

  const items = [
    { key: '1', icon: <AppstoreOutlined />, label: 'Dashboard' },
    ...(teamRoleOf(currentUser) === 'Admin' || isCandidate
      ? [{ key: '13', icon: <WalletOutlined />, label: 'iParley Wallet' }]
      : []),
    // ...(isCandidate
    //       ? [{ key: '2', icon: <FileSearchOutlined />, label: 'Find Job' }]
    //       : []),
    // ...(!isCandidate
    //       ? [{ key: '3', icon: <FileAddOutlined />, label: 'Post Job' }]
    //       : []),
    {
      key: '4',
      icon: <MailOutlined />,
      label: (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <span>Messages</span>
          <Badge count={totalUnreadConversations || currentUserNotificationCount} />
        </div>
      ),
    },
    { key: '5', icon: <CalendarOutlined />, label: 'Calendar' },
    ...(!isCandidate && teamRoleOf(currentUser) !== 'Tech Sales'
          ? [{ key: '6', icon: <TeamOutlined />, label: 'Manage Users' }]
          : []),
    // { key: '7', icon: <SafetyOutlined />, label: 'Verifications' },
    ...(isCustomer && customerType?.toLowerCase().includes('implementation') && (teamRoleOf(currentUser) === 'Admin' || teamRoleOf(currentUser) === 'Tech Acquisition' || teamRoleOf(currentUser) === 'Tech Manager')
          ? [{ key: '12', icon: <FileAddOutlined />, label: 'RFP Posts' }]
          : []),
    ...(isCustomer && customerType?.toLowerCase().includes('staffing') && (teamRoleOf(currentUser) === 'Admin' || teamRoleOf(currentUser) === 'HR/Talent Acquisition' || teamRoleOf(currentUser) === 'Hiring Manager')
          ? [{ key: '11', icon: <FileAddOutlined />, label: 'Job Posts' }]
          : []),
    // { key: '11', icon: <PicCenterOutlined />, label: 'Your Listings' },
    ...networkItem,
    { key: '8', icon: <UserOutlined />, label: 'Profile' },
    {
      key: '9',
      icon: <SettingOutlined />,
      label: 'Account Settings',
      children: [
        // { key: '9a', label: 'Contact details' },
        // { key: '9b', label: 'Password' },
        { key: '9c', label: 'Subscription' },
        // { key: '9d', label: 'Message Packs' },
        { key: '9d', label: 'Add-ons' },
        { key: '9e', label: 'SMS Consent' },
        { key: '9f', label: 'Payments History' },
      ],
    },
    { key: '10', icon: <PoweroffOutlined style={{ color: 'red' }} />, label: 'Log Out' },
  ];

  return (
    <>
      <div ref={menuRef} className={css.menuSide}>
        <Menu
          mode={isMobile ? 'horizontal' : 'inline'}
          inlineCollapsed={!isMobile && collapsed}
          items={items}
          onClick={onMenuClick}
          selectedKeys={[onNetworkPage ? (networkNav?.kind === 'direct' ? MY_NETWORK_ITEM_KEY : NETWORK_ITEM_KEY) : selectedTab]}
          openKeys={openKeys}
          onOpenChange={onMenuOpen}
        />
        <div className={css.menuCollapsecontainer}>
          <Button type="text" block size="large" onClick={toggleCollapsed}>
            {collapsed ? (
              <DoubleRightOutlined />
            ) : (
              <>
                <DoubleLeftOutlined /> Collapse Menu
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  );
};



const mapStateToProps = state => {
  const { currentUserNotificationCount, currentUser } = state.user;
  const conversations = state.InboxPage?.conversations || [];
  const currentUserAdminId = state.InboxPage?.profileListingAdminId || currentUser?.id?.uuid;

  const totalUnreadConversations = conversations.filter(conv => {
    const me = conv.participants?.find(p => p.userId === currentUserAdminId);
    return (me?.unreadCount || 0) > 0;
  }).length;

  return {
    currentUserNotificationCount,
    totalUnreadConversations,
    conversationsBootstrapped: state.InboxPage?.profileListingAdminId !== null,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onLogout: historyPush => dispatch(logout(historyPush)),
    onBootstrapConversations: () => dispatch(loadInboxData()),
  };
};

const DashboardSideNav = compose(connect(mapStateToProps,mapDispatchToProps))(DashboardSideNavComponent);

export default DashboardSideNav;
